import { version } from '../../package.json';
import { buildDelBinario } from './buildNativo';
import { esApp, plataforma } from './plataforma';

/**
 * Dentro de la app, cada llamada a la API dice qué binario la hace y desde dónde. En la web no se
 * toca nada. Qué es cada número, con ejemplos: `docs/app-movil/VERSION_MINIMA.md`.
 *
 * - `X-Client-Version`: el build NATIVO del binario instalado (`CFBundleVersion` / `versionCode`,
 *   hoy `11`). El mismo número que se compara con la versión mínima (`buildDelBinario`), aunque esa
 *   comprobación no usa la cabecera: es la app la que se compara. Si no se pudo leer, no se manda
 *   (y se reintenta en la siguiente llamada).
 * - `X-Client-Web-Version`: la del `package.json` con la que se compiló la web que va dentro (hoy
 *   `9.25.2`). Hoy cambia a la vez que el build; con OTA (Capawesome, Capgo…) la web se
 *   actualizaría sin pasar por la tienda y dejarían de coincidir.
 * - `X-Client-Platform`: `ios` o `android`. Nunca `web`.
 *
 * Ninguna es necesaria para bloquear: sirven para saber desde el servidor (logs, New Relic) qué
 * binario llama, y para que algún día el servidor pueda rechazar uno.
 *
 * 🔴 Tiene que ir en el PRIMER binario que llegue a las tiendas. Un binario publicado no se puede
 * cambiar: si no manda su versión, el servidor nunca podrá distinguirlo ni pedirle que se
 * actualice. Que el servidor haga algo con estas cabeceras puede llegar después.
 *
 * Se envuelve `fetch` una vez, al arrancar, porque no hay cliente HTTP común: las llamadas están
 * repartidas por `src/api/`. Solo se tocan las que van a nuestra API; PostHog, Clarity y el resto
 * de terceros salen como siempre.
 */
export const instalarCabecerasDeCliente = (
	urlDeLaApi: string | undefined = process.env.REACT_APP_BASE_API
): void => {
	// En la web no: una cabecera propia obliga al navegador a preguntar por CORS antes de cada
	// llamada. La versión solo hace falta para distinguir los binarios de las tiendas.
	if (!esApp() || !urlDeLaApi) {
		return;
	}

	const fetchOriginal = window.fetch.bind(window);

	window.fetch = (destino, opciones) => {
		const peticion = destino instanceof Request ? destino : undefined;
		const url = peticion ? peticion.url : String(destino);

		if (!url.startsWith(urlDeLaApi)) {
			return fetchOriginal(destino, opciones);
		}

		return buildDelBinario().then((numero) => {
			const headers = new Headers(opciones?.headers ?? peticion?.headers);
			if (numero !== null) {
				headers.set('X-Client-Version', String(numero));
			}
			headers.set('X-Client-Web-Version', version);
			headers.set('X-Client-Platform', plataforma());

			return fetchOriginal(destino, { ...opciones, headers });
		});
	};
};
